import React from "react";
import { useTranslation } from "react-i18next";

interface Note {
  id: number;
  content: string;
  author_name?: string;
  created_at: string;
}

interface Company {
  company_name?: string;
  name?: string;
  industry?: string;
  company_size?: string;
  employee_count?: number;
  founded_year?: number;
  description?: string;
  website?: string;
}

interface CompanyInfoSectionProps {
  company: Company;
  notes?: Note[];
  maxNotesPreview?: number;
}

/**
 * Displays company information and notes in the TechCheck form.
 * Shows company details like name, industry, size, and recent notes.
 */
export const CompanyInfoSection: React.FC<CompanyInfoSectionProps> = ({
  company,
  notes = [],
  maxNotesPreview = 3,
}) => {
  const { t } = useTranslation(["recruiting"]);
  return (
    <div className="tech-check-section company-info-section">
      <h3>{t("techCheck.companyInfo.title")}</h3>

      <div className="info-field">
        <span className="info-label">
          {t("techCheck.companyInfo.companyName")}
        </span>
        <div className="info-value">
          {company.company_name || company.name || "N/A"}
        </div>
      </div>

      {company.industry && (
        <div className="info-field">
          <span className="info-label">
            {t("techCheck.companyInfo.industry")}
          </span>
          <div className="info-value">{company.industry}</div>
        </div>
      )}

      {company.company_size && (
        <div className="info-field">
          <span className="info-label">
            {t("techCheck.companyInfo.companySize")}
          </span>
          <div className="info-value">
            {company.company_size.charAt(0).toUpperCase() +
              company.company_size.slice(1)}
          </div>
        </div>
      )}

      {company.employee_count && (
        <div className="info-field">
          <span className="info-label">
            {t("techCheck.companyInfo.employees")}
          </span>
          <div className="info-value">
            {company.employee_count.toLocaleString()}
          </div>
        </div>
      )}

      {company.founded_year && (
        <div className="info-field">
          <span className="info-label">
            {t("techCheck.companyInfo.founded")}
          </span>
          <div className="info-value">{company.founded_year}</div>
        </div>
      )}

      {company.description && (
        <div className="info-field">
          <span className="info-label">
            {t("techCheck.companyInfo.description")}
          </span>
          <div className="info-value description-text">
            {company.description}
          </div>
        </div>
      )}

      {company.website && (
        <div className="info-field">
          <span className="info-label">
            {t("techCheck.companyInfo.website")}
          </span>
          <div className="info-value">
            <a href={company.website} target="_blank" rel="noopener noreferrer">
              {company.website}
            </a>
          </div>
        </div>
      )}

      {notes.length > 0 && (
        <div className="info-field">
          <span className="info-label">
            {t("techCheck.companyInfo.comments")}
          </span>
          <div className="company-notes-preview">
            {notes.slice(0, maxNotesPreview).map((note) => (
              <div key={note.id} className="note-preview-item">
                <p className="note-content">{note.content}</p>
                <span className="note-meta">
                  {note.author_name} -{" "}
                  {new Date(note.created_at).toLocaleDateString()}
                </span>
              </div>
            ))}
            {notes.length > maxNotesPreview && (
              <p className="more-notes">
                {t("techCheck.companyInfo.moreComments", {
                  count: notes.length - maxNotesPreview,
                })}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CompanyInfoSection;
